"use client";

/**
 * UIX ThemeInjector
 *
 * Ezt a komponenst az alkalmazás gyökerébe kell helyezni.
 * Az aktív UixTheme alapján CSS változókat injektál a :root-ba,
 * amelyeket aztán a .styles.ts fájlok CSS var() hivatkozásokon
 * keresztül olvasnak (ha arra frissülnek a 5.4-es tesztben).
 *
 * Ezen felül a google fonts-ot is betölti dinamikusan.
 */

import React, { useEffect } from "react";
import { useUixTheme } from "../core/ThemeContext";
import { UixTheme } from "./theme.types";

/**
 * Témából CSS változók generálása
 */
function buildCssVariables(theme: UixTheme): Record<string, string> {
    const { colors, typography, layout, components } = theme;

    return {
        // === Színek ===
        "--uix-color-surface": colors.surface,
        "--uix-color-background": colors.background,
        "--uix-color-border": colors.border,
        "--uix-color-text-primary": colors.textPrimary,
        "--uix-color-text-secondary": colors.textSecondary,
        "--uix-color-text-inverse": colors.textInverse,
        "--uix-color-brand": colors.brandPrimary,
        "--uix-color-surface-hover": colors.surfaceHover,

        // === Badge/Sale ===
        "--uix-badge-sale-bg": colors.sale,
        "--uix-badge-sale-text": colors.saleText,

        // === Tipográfia ===
        "--uix-font-family": typography.fontFamily,
        "--uix-font-size-base": typography.baseFontSize,
        "--uix-line-height": typography.lineHeight,

        // === Layout / Radius ===
        "--uix-radius-card": layout.radiusCard,
        "--uix-radius-button": layout.radiusButton,
        "--uix-radius-badge": layout.radiusBadge,

        // === Árnyék ===
        "--uix-shadow-card": layout.shadowCard,
        "--uix-shadow-btn": layout.shadowButton,

        // === Komponens: Button ===
        "--uix-btn-primary-bg": components.button.primaryBg,
        "--uix-btn-primary-text": components.button.primaryText,
        "--uix-btn-primary-hover": components.button.primaryHover,
        "--uix-btn-primary-border": components.button.primaryBorder,

        // === Komponens: Card ===
        "--uix-card-bg": components.card.bg,
        "--uix-card-border": components.card.border,
        "--uix-card-shadow-hover": components.card.hoverShadow,

        // === Komponens: Badge ===
        "--uix-badge-bg": components.badge.saleBg,
        "--uix-badge-text": components.badge.saleText,
    };
}

export const UixThemeInjector: React.FC = () => {
    const { theme } = useUixTheme();

    // Dinamikus Google Fonts URL összeállítása súlyokkal (Phase 6.3.5)
    // Megjegyzés: A 400, 500, 600, 700, 800 súlyokat töltjük be a prémium megjelenéshez
    const fontFamily = theme.typography.fontFamily;
    const fontName = fontFamily.replace(/['"]/g, "").split(",")[0].trim();
    const fontUrl = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, "+")}:wght@400;500;600;700;800&display=swap`;

    const cssVars = buildCssVariables(theme);
    const cssString = Object.entries(cssVars)
        .map(([key, val]) => `  ${key}: ${val};`)
        .join("\n");

    const fullCss = `:root {\n${cssString}\n}\n\n* { font-family: var(--uix-font-family); }\n\n${theme.customCss || ""}`;

    return (
        <>
            {/* Preconnect a gyorsabb betöltésért */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

            {/* Google Font betöltése súlyokkal */}
            <link id="uix-google-font" rel="stylesheet" href={fontUrl} />

            {/* Dinamikus téma változók és globális font-family */}
            <style
                id="uix-theme-variables"
                dangerouslySetInnerHTML={{ __html: fullCss }}
            />
        </>
    );
};
